import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { checkJiraAccess } from '../services/jiraService';
import { JiraAccessResponse } from '../types';

export type JiraAccessStatus = 'checking' | 'granted' | 'denied' | 'unauthenticated' | 'error';

export interface UseJiraAccessReturn {
  status: JiraAccessStatus;
  accessData: JiraAccessResponse | null;
  errorMessage: string | null;
  checkAccess: (forceTokenRefresh?: boolean) => Promise<void>;
  retry: () => Promise<void>;
}

export const useJiraAccess = (): UseJiraAccessReturn => {
  const { user, firebaseUser } = useAuth();
  const isAuth = Boolean(user || firebaseUser);

  const [status, setStatus] = useState<JiraAccessStatus>(isAuth ? 'checking' : 'unauthenticated');
  const [accessData, setAccessData] = useState<JiraAccessResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Prevent race conditions between requests
  const reqIdRef = useRef(0);

  const checkAccess = useCallback(async (_forceTokenRefresh = false) => {
    if (!isAuth) {
      setStatus('unauthenticated');
      setAccessData(null);
      setErrorMessage('Please sign in with your Google account to access Jira features.');
      return;
    }

    const currentReqId = ++reqIdRef.current;
    setStatus('checking');
    setErrorMessage(null);

    try {
      const data = await checkJiraAccess();
      if (reqIdRef.current !== currentReqId) return;

      if (data.has_access === false || data.access === false || data.is_member === false) {
        setStatus('denied');
        setAccessData(data);
        setErrorMessage(
          data.message ||
            'Your account is not an active member of the ScrumAIApp Atlassian organization. Please request an invite to scrumaiapp.atlassian.net.'
        );
      } else {
        setStatus('granted');
        setAccessData(data);
        setErrorMessage(null);
      }
    } catch (err: any) {
      if (reqIdRef.current !== currentReqId) return;

      console.warn('Jira access check error:', err);
      const statusCode = err?.status;
      if (statusCode === 401) {
        setStatus('unauthenticated');
        setErrorMessage('Your session has expired. Please sign in again.');
      } else if (statusCode === 403) {
        setStatus('denied');
        setErrorMessage(
          'Access denied: Your account has not been added to the ScrumAIApp Atlassian organization. Contact your administrator to receive an invitation.'
        );
      } else {
        setStatus('error');
        setErrorMessage(err?.message || 'Unable to connect to Jira services. Please try again.');
      }
    }
  }, [isAuth]);

  useEffect(() => {
    if (isAuth) {
      checkAccess();
    } else {
      setStatus('unauthenticated');
      setAccessData(null);
      setErrorMessage(null);
    }
  }, [isAuth, checkAccess]);

  const retry = useCallback(() => checkAccess(true), [checkAccess]);

  return {
    status,
    accessData,
    errorMessage,
    checkAccess,
    retry
  };
};
